import React, { useContext } from 'react';
import { KaEmployeeTemplate } from '../UserAccess/KaEmployeeTemplate';
import { UserTemplate } from '../UserAccess/UserTemplate';
import NavPageLink from './NavPageLink'
import NavSection from './NavSection';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { LoginType, UserContext } from '../UserAccess/UserContext';
import { SidebarContext } from './SidebarContext';
import { IntegrationNavSubsections } from './IntegrationNavSubsections';

const UserConstellationLinks = ({ showDataSharing }: { showDataSharing: boolean }) => (
  <>
    <NavPageLink
      defaultPath="/"
      iconName="speedometer2"
      linkDisplayText="Dashboard" />
    <NavPageLink
      defaultPath="/reports"
      iconName="file-text"
      linkDisplayText="Reports" />
    <NavPageLink
      defaultPath="/branches"
      iconName="geo-alt"
      linkDisplayText="Branches" />
    <NavPageLink
      defaultPath="/customers"
      iconName="people"
      linkDisplayText="Customers" />
    <NavPageLink
      defaultPath="/drivers"
      iconName="people"
      linkDisplayText="Drivers" />
    <NavPageLink
      defaultPath="/orders"
      iconName="file-ruled"
      linkDisplayText="Orders" />
    <NavPageLink
      defaultPath="/orderTransactions"
      iconName="file-earmark-ruled"
      linkDisplayText="Order Transactions" />
    <NavPageLink
      defaultPath="/purchaseOrders"
      iconName="file-ruled"
      linkDisplayText="Purchase Orders" />
    <NavPageLink
      defaultPath="/products"
      iconName="diagram-3"
      linkDisplayText="Products" />
    {showDataSharing &&
      <NavPageLink
        defaultPath="/sharedproducts"
        iconName="link-45deg"
        linkDisplayText="Shared Products" />}
    <NavPageLink
      defaultPath="/recipes"
      iconName="diagram-3"
      linkDisplayText="Recipes" />
    <NavPageLink
      defaultPath="/transports"
      iconName="truck"
      linkDisplayText="Transports" />
    <NavPageLink
      defaultPath="/carriers"
      iconName="truck"
      linkDisplayText="Carriers" />
    <NavPageLink
      defaultPath="/applicators"
      iconName="truck"
      linkDisplayText="Applicators" />
  </>
);

const KaEmployeeLinks = () => {
  const employee = useKaEmployee();

  return <>
    <NavSection title="Constellation" defaultExpanded>
      <NavPageLink
        businessNotRequired
        defaultPath="/employee/business"
        linkDisplayText="Businesses" />
      {employee.canViewNotifications &&
        <NavPageLink
          businessNotRequired
          defaultPath="/employee/siteNotifications"
          linkDisplayText="Site Notifications" />}
      {employee.canViewSiteStatus &&
        <NavPageLink
          businessNotRequired
          defaultPath="/employee/sitesStatus"
          linkDisplayText="Sites Status" />}
      {employee.canViewOrders &&
        <NavPageLink
          defaultPath="/employee/orders"
          linkDisplayText="Orders" />}
    </NavSection>
    {employee.canViewIntegrations &&
      <NavSection title="Integrations" defaultExpanded topDivider>
        <IntegrationNavSubsections
          routePrefix="/employee"
          integrations={employee.selectedBusiness?.integrations.map(i => i.name) ?? []} />
        <NavPageLink
          businessNotRequired
          defaultPath="/employee/integrations"
          iconName="plug"
          linkDisplayText="Other Integrations" />
      </NavSection>}
  </>
}

export function NavSideMenu() {
  const { user } = useContext(UserContext);
  const { isCollapsed, toggleSidebar } = useContext(SidebarContext);
  const selectedUserBusiness = user?.userType === LoginType.User ? user.selectedBusiness : null;
  const showDataSharing = selectedUserBusiness?.usesDataSharing ?? false;

  return <nav className={`navbar navbar-expand-sm navbar-sidebar navbar-vertical navbar-light bg-white border-bottom border-end${isCollapsed ? ' sidebar-collapsed-nav' : ''}`}>
    {!isCollapsed && <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#sideNavbar" aria-controls="sideNavbar" aria-expanded="false" aria-label="Toggle navigation">
      <span className="navbar-toggler-default">
        <i className="bi-list"></i>
      </span>
      <span className="navbar-toggler-toggled">
        <i className="bi-x"></i>
      </span>
    </button>}
    <div className="collapse navbar-collapse navbar-sidebar-aside-body" id="sideNavbar">
      <div className="sticky-top w-100 bg-white">
        <button
          className="btn btn-ghost-secondary btn-icon"
          onClick={toggleSidebar}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <i className={`bi-${isCollapsed ? 'layout-sidebar' : 'layout-sidebar-inset'} fs-4`}></i>
        </button>
      </div>
      <ul className="navbar-nav nav nav-vertical nav-tabs nav-tabs-borderless pt-0">
        <KaEmployeeTemplate>
          <KaEmployeeLinks />
        </KaEmployeeTemplate>
        <UserTemplate>
          <NavSection title="Constellation" defaultExpanded>
            <UserConstellationLinks showDataSharing={showDataSharing} />
          </NavSection>
          {selectedUserBusiness?.administrator &&
            <NavSection title="Integrations" defaultExpanded topDivider>
              <IntegrationNavSubsections routePrefix="" integrations={selectedUserBusiness.integrations.map(i => i.name)} />
              {selectedUserBusiness.integrations
                .filter(i => i.name !== 'FieldAlytics')
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(i =>
                  <NavPageLink key={i.name} href={i.configurationUri} iconName="plug" linkDisplayText={i.name} />)}
              <NavPageLink
                defaultPath="/extIntegrations"
                iconName="plug"
                linkDisplayText="External Integrations" />
            </NavSection>}
        </UserTemplate>
      </ul>
    </div>
    <div data-testid="logo-container" className={`sticky-bottom w-100 bg-white${isCollapsed ? ' d-flex justify-content-center' : ''}`}>
      <a href="https://www.kahlerautomation.com" target="_blank" rel="noreferrer" aria-label="Logo" title="Kahler Automation">
        <img className={`m-2${isCollapsed ? '' : ' d-none'}`} src="/KA-RGB-color_logo-minimized.png" alt="Minimized Logo" style={{ width: '3.5rem' }} />
        <img className={`m-2${isCollapsed ? ' d-none' : ''}`} src="/KA-RGB-color_logo.png" alt="Logo" width='100' style={{ minWidth: '15rem', maxWidth: '15rem' }} />
      </a>
    </div>
  </nav>
}
